import { useState } from 'react';
import { Box } from '@mui/material';
import { useTenant } from '@/tenant/useTenant';
import { duration, easing } from '@/theme/motion';
import { initials } from '@/utils/initials';
import { PlatformMark } from './PlatformMark';

/**
 * The current space's mark: the tenant's icon/logo, a monogram in the tenant's
 * primary color when no image was uploaded, or the platform mark on the console.
 */
export function BrandMark({ size = 40 }: { size?: number }) {
  const { config, isPlatform, isLanding, displayName } = useTenant();
  const [failed, setFailed] = useState(false);
  const src = config.branding?.iconUrl ?? config.branding?.logoUrl;

  if (isPlatform || isLanding) return <PlatformMark size={size} />;

  if (src && !failed) {
    return (
      <Box
        component="img"
        src={src}
        alt=""
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        sx={{
          display: 'block',
          flexShrink: 0,
          objectFit: 'contain',
          borderRadius: `${Math.round(size * 0.27)}px`,
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        width: size,
        height: size,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${Math.round(size * 0.3)}px`,
        bgcolor: theme.vars.palette.primary.main,
        color: theme.vars.palette.primary.contrastText,
        fontWeight: 700,
        fontSize: size * 0.4,
        letterSpacing: '-0.02em',
      })}
    >
      {initials(displayName)}
    </Box>
  );
}

/** Pulsing mark for splash and loading screens. */
export function BrandPulse({ size = 64 }: { size?: number }) {
  return (
    <Box
      sx={{
        animation: `brand-pulse ${duration.long4 * 3}ms ${easing.standard} infinite`,
        '@keyframes brand-pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(0.9)', opacity: 0.75 },
        },
      }}
    >
      <BrandMark size={size} />
    </Box>
  );
}
